import { useEffect, useState } from 'react';
import { api, type FilaPareja, type Yo } from '../services/api';
import { Aviso, Cargando, Tarjeta, Titulo, Vacio } from '../components/ui';

/**
 * Las parejas que más ganan jugando juntas. Por resultados, no por Elo: la
 * cuenta y el porqué están en nucleo/parejas.ts.
 */
export function Parejas({ yo, onVerPerfil }: { yo: Yo; onVerPerfil: (id: number) => void }) {
  const [datos, setDatos] = useState<{ minimo: number; parejas: FilaPareja[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .parejas()
      .then(setDatos)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Error'));
  }, []);

  if (error) return <Aviso tono="error">{error}</Aviso>;
  if (!datos) return <Cargando que="Juntando parejas" />;

  return (
    <div className="flex flex-col gap-4">
      <Titulo>Mejores parejas</Titulo>

      {datos.parejas.length === 0 ? (
        <Vacio>
          Todavía ninguna pareja jugó {datos.minimo} partidos juntos. Cuando alguna llegue,
          aparece acá.
        </Vacio>
      ) : (
        <Tarjeta sinRelleno>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-(--color-heroe) text-[11px] uppercase tracking-wide">
                <th className="py-2 pl-3 text-left font-normal">#</th>
                <th className="py-2 text-left font-normal">Pareja</th>
                <th className="py-2 text-right font-normal">PJ</th>
                <th className="py-2 text-right font-normal">G-P</th>
                <th className="py-2 pr-3 text-right font-normal">%</th>
              </tr>
            </thead>
            <tbody>
              {datos.parejas.map((fila) => {
                const mia = fila.jugadores.some((j) => j.id === yo.jugador.id);
                return (
                  <tr
                    key={fila.jugadores.map((j) => j.id).join('-')}
                    className={`border-b border-(--color-borde)/40 last:border-0 ${mia ? 'bg-white/5' : ''}`}
                  >
                    <td className="py-2 pl-3 font-titulo text-2xl tabular-nums">{fila.posicion}</td>
                    <td className="py-3">
                      {fila.jugadores.map((j, i) => (
                        <span key={j.id}>
                          {i > 0 && <span className="text-(--color-apagado)"> · </span>}
                          <button
                            onClick={() => onVerPerfil(j.id)}
                            className="underline decoration-(--color-borde) underline-offset-2"
                          >
                            {j.nombre}
                          </button>
                        </span>
                      ))}
                    </td>
                    <td className="py-3 text-right tabular-nums text-(--color-apagado)">
                      {fila.partidos}
                    </td>
                    <td className="py-3 text-right tabular-nums text-(--color-apagado)">
                      {fila.ganados}-{fila.perdidos}
                    </td>
                    <td className="py-3 pr-3 text-right font-semibold tabular-nums">
                      {Math.round((100 * fila.ganados) / fila.partidos)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Tarjeta>
      )}

      <Aviso>
        Entran las parejas con {datos.minimo} partidos o más juntos. Se ordenan por cuánto ganan,
        pero con pocos partidos pesa menos: dos de dos no le gana a nueve de diez. Para ver con
        quién rinde más cada uno según su nivel, está el perfil.
      </Aviso>
    </div>
  );
}
